import { DateTime } from 'luxon';
import { CalendarEvent } from './calendar-events';
import { withMeetingLinkLine } from './calendar-description';

/**
 * "Add to calendar" links for one event (workorder-calendar-quick-wins-a-2026-11-02, task 1c): Google Calendar
 * and Outlook on the web open their compose form prefilled. Wall time as entered - Google gets the zone as
 * ctz, Outlook knows no zone parameter and gets the offset of header.timeZone in the timestamp.
 * Meeting link (calendar quick wins C, event.url): the location when the event has none, "Online: …" as the first
 * line of details / body - before the cut to CALENDAR_LINK_DETAILS_MAX, so the link is never cut off.
 */
export interface CalendarLinkOptions {
    /** the finished description text (the client builds it, later the server) */
    description: string;
    /** added to the title of a maybe event, e.g. "(if necessary)" */
    maybeSuffix: string;
    /** vote.online, e.g. "Online:" - in front of the meeting link in details / body */
    onlineLabel: string;
}

export interface CalendarLinks {
    google: string;
    outlook: string;
}

/** long urls break at Google and Outlook - details/body are cut to this many characters */
export const CALENDAR_LINK_DETAILS_MAX = 1500;

const GOOGLE_BASE = 'https://calendar.google.com/calendar/render';
const OUTLOOK_BASE = 'https://outlook.live.com/calendar/0/action/compose';

export function calendarLinks(event: CalendarEvent, options: CalendarLinkOptions): CalendarLinks {
    const summary = event.tentative ? `${event.summary} ${options.maybeSuffix}` : event.summary;
    const details = truncate(withMeetingLinkLine(options.description, event.url, options.onlineLabel), CALENDAR_LINK_DETAILS_MAX);
    const location = event.location ?? event.url;
    return {
        google: buildUrl(GOOGLE_BASE, [
            ['action', 'TEMPLATE'],
            ['text', summary],
            ['dates', `${googleDate(event.start, event.allDay)}/${googleDate(event.end, event.allDay)}`],
            ['details', details],
            ['location', location],
            ['ctz', event.allDay ? undefined : event.timeZone],
        ]),
        outlook: buildUrl(OUTLOOK_BASE, [
            ['subject', summary],
            ['startdt', outlookDate(event.start, event)],
            ['enddt', outlookDate(event.end, event)],
            ['body', details],
            ['location', location],
            ['allday', event.allDay ? 'true' : 'false'],
            ['path', '/calendar/action/compose'],
            ['rru', 'addevent'],
        ]),
    };
}

/** "20261114T190000" with time, "20261114" all day (the end exclusive, as in the event) */
function googleDate(value: string, allDay: boolean): string {
    const wallTime = DateTime.fromISO(value, { zone: 'utc' });
    return wallTime.toFormat(allDay ? 'yyyyLLdd' : "yyyyLLdd'T'HHmmss");
}

/** "2026-11-14T19:00:00+01:00" with a zone, without a zone the wall time without offset, all day the date only */
function outlookDate(value: string, event: CalendarEvent): string {
    if (event.allDay) return DateTime.fromISO(value, { zone: 'utc' }).toISODate();
    if (event.timeZone) {
        const zoned = DateTime.fromISO(value, { zone: event.timeZone });
        if (zoned.isValid) return zoned.toISO({ suppressMilliseconds: true });
    }
    return DateTime.fromISO(value, { zone: 'utc' }).toFormat("yyyy-LL-dd'T'HH:mm:ss");
}

function buildUrl(base: string, parameters: [string, string | undefined][]): string {
    const query = parameters
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');
    return `${base}?${query}`;
}

function truncate(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    let cut = text.slice(0, maxLength - 1);
    // never leave half of a surrogate pair (emoji) behind
    if (/[\uD800-\uDBFF]$/.test(cut)) cut = cut.slice(0, -1);
    return cut + '…';
}
